import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { VerticalContainer } from '@/components';
import { cn } from '@/lib/cn';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import { stripHtmlTags } from '@/caregiverSpace/shared/lib';
import { CaregiverPhraseDto } from '@/caregiverSpace/shared/model';

import { SlashMenuPosition } from '../lib/editor/useSlashMenu';

type SlashMenuProps = {
    isOpen: boolean;
    position: SlashMenuPosition;
    phrases: CaregiverPhraseDto[];
    selectedIndex: number;
    onSelect: (phrase: CaregiverPhraseDto) => void;
    onClose: () => void;
};

export const SlashMenu = ({
    isOpen,
    position,
    phrases,
    selectedIndex,
    onSelect,
    onClose,
}: SlashMenuProps) => {
    const selectedItemRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (isOpen && selectedItemRef.current) {
            selectedItemRef.current.scrollIntoView({ block: 'nearest' });
        }
    }, [isOpen, selectedIndex]);

    useEffect(() => {
        if (!isOpen) return;

        const handleMouseDown = (e: MouseEvent) => {
            if (!(e.target as Element).closest('[data-slash-menu]')) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const ITEM_HEIGHT = 46;
    const MAX_VISIBLE = 3;
    const MENU_OFFSET = 4;

    const menuMaxHeight =
        Math.max(1, Math.min(phrases.length, MAX_VISIBLE)) * ITEM_HEIGHT;
    const wouldOverflow =
        position.top + MENU_OFFSET + menuMaxHeight > window.innerHeight;
    const menuTop = wouldOverflow
        ? position.top - menuMaxHeight
        : position.top + MENU_OFFSET;

    return createPortal(
        <div
            data-slash-menu
            style={{
                position: 'fixed',
                top: menuTop,
                left: position.left,
                maxHeight: `${menuMaxHeight}px`,
            }}
            className="caregiver-space z-100 w-[352px] overflow-y-auto rounded-lg bg-(--color-bg-surface) shadow-lg"
        >
            {phrases.length === 0 ? (
                <VerticalContainer
                    spacing="none"
                    className="px-2 py-0.5 text-(--color-text-muted)"
                >
                    <span className="truncate text-(--color-text-primary)">
                        {R.NoResultsFound}
                    </span>
                    <span className="w-full truncate text-(--color-text-muted)">
                        {R.SlashMenuNoResultsHint}
                    </span>
                </VerticalContainer>
            ) : (
                phrases.map((phrase, index) => (
                    <button
                        key={phrase.Id}
                        ref={index === selectedIndex ? selectedItemRef : null}
                        type="button"
                        className={cn(
                            'flex w-full flex-col px-2 py-0.5 text-left',
                            index === selectedIndex
                                ? 'bg-(--color-bg-subtle)'
                                : 'hover:bg-(--color-bg-subtle)',
                        )}
                        onMouseDown={(e) => {
                            // Prevent the editor from losing focus before we insert.
                            e.preventDefault();
                            onSelect(phrase);
                        }}
                    >
                        <span className="w-full truncate text-(--color-text-primary)">
                            /{phrase.Title}
                        </span>
                        <span className="w-full truncate text-(--color-text-muted)">
                            {stripHtmlTags(phrase.Content)
                                .split('\n')
                                .find((l) => l.trim())}
                        </span>
                    </button>
                ))
            )}
        </div>,
        document.body,
    );
};
